import type { CSSProperties } from 'react';
import chroma from 'chroma-js';
import { validateUrl } from '@/utils/validate-url';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';
import ContentEditable from '../content-editable';
import LogoWidget from './logo-widget';
import type { TextWidgetProps } from './widget-types';

// what the card's text is actually drawn on: its own background, mixed over the body when translucent
function cardSurface(background: string | undefined, bodyColor: string | undefined): string | undefined {
  const body = bodyColor && chroma.valid(bodyColor) ? bodyColor : undefined;
  if (!background || !chroma.valid(background)) return body;
  const card = chroma(background);
  if (card.alpha() >= 1 || !body) return card.alpha(1).hex();
  return chroma.mix(body, card.alpha(1), card.alpha(), 'rgb').hex();
}

// the footer's text colors aren't editable, only its background - so they're kept readable on it
const readableOn = (color: string | undefined, surface: string | undefined) =>
  color && surface && chroma.valid(color) && chroma.valid(surface) ? getRelativeContrastColor(color, surface) : color;

export interface FooterWidgetProps extends TextWidgetProps {
  // '' shows the "Logo" placeholder; undefined (the text-only footers templates ship with) shows nothing
  src?: string;
  // unset when the campaign hides the Brandlive logo
  brandliveLogo?: string;
  showLegalLink?: boolean;
  privacyPolicyUrl?: string;
  privacyPolicyLabel?: string;
  // what the block sits on - the link, and a card without its own background, stay readable against it
  bodyColor?: string;
  accentColor?: string;
}

export default function FooterWidget({
  blockId,
  value,
  setValue,
  color,
  fontFamily,
  fontSize = '11px',
  fontWeight = 500,
  lineHeight,
  letterSpacing = '0.2px',
  textAlign,
  textTransform,
  fontStyle,
  whiteSpace = 'pre-line',
  background,
  border,
  borderColor,
  borderRadius,
  padding = '20px 56px',
  margin,
  fitContent = false,
  align,
  opacity,
  src,
  brandliveLogo,
  showLegalLink,
  privacyPolicyUrl,
  privacyPolicyLabel = 'Privacy Policy',
  bodyColor,
  accentColor,
}: FooterWidgetProps) {
  const fitAlign = align ?? 'center';
  const surface = cardSurface(background, bodyColor);
  const textColor = readableOn(color, surface);
  const linkColor = readableOn(color, bodyColor);
  const placeholderColor = readableOn(accentColor, surface);
  const boxStyle: CSSProperties = {
    background,
    border: border ? `${border} solid ${borderColor ?? 'transparent'}` : undefined,
    borderRadius,
    padding,
  };
  const textStyle: CSSProperties = {
    color: textColor,
    fontFamily,
    fontSize,
    fontWeight,
    lineHeight,
    letterSpacing,
    textAlign: align ?? textAlign,
    textTransform,
    fontStyle,
    whiteSpace,
    opacity,
    wordWrap: 'break-word',
  };
  const linkTextStyle: CSSProperties = { color: linkColor, fontFamily, fontSize: '10px', fontWeight, letterSpacing };
  const showPrivacyLink = !!showLegalLink && !!privacyPolicyUrl && validateUrl(privacyPolicyUrl);

  return (
    <table data-component="FooterWidget" role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
      <tbody>
        <tr>
          <td
            align={fitContent ? fitAlign : undefined}
            style={{ padding: margin, textAlign: fitContent ? fitAlign : undefined }}
          >
            <table
              width={fitContent ? undefined : '100%'}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ margin: fitContent && fitAlign === 'center' ? '0 auto' : undefined }}
            >
              <tbody>
                <tr>
                  <td data-block-id={blockId} style={boxStyle}>
                    {/* the logo and its placeholder are both 24px tall, so adding or removing one doesn't move the text */}
                    {src ? (
                      <LogoWidget src={src} height={24} align={align ?? 'left'} padding="0 0 20px 0" />
                    ) : src === '' ? (
                      <div
                        style={{
                          paddingBottom: '20px',
                          textAlign: align ?? 'left',
                          fontFamily: 'Georgia, serif',
                          fontSize: '18px',
                          lineHeight: '24px',
                          fontWeight: 700,
                          color: placeholderColor,
                        }}
                      >
                        Logo
                      </div>
                    ) : null}
                    <ContentEditable value={value} setValue={setValue} style={textStyle} keepLineWhenEmpty />
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        {/* the strip below the card, outside its spacing - the legal link centered, the Brandlive logo on the
            right. The empty cell on the left matches the logo's, so the link stays centered on the row */}
        {(showPrivacyLink || brandliveLogo) && (
          <tr>
            <td style={{ padding: '20px 56px' }}>
              <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                <tbody>
                  <tr>
                    <td width="25%" />
                    <td align="center" valign="middle" style={{ textAlign: 'center' }}>
                      {showPrivacyLink && (
                        // a real link only in the sent email - in the editor it would steal the click, as ImageWidget's does
                        <a
                          href={privacyPolicyUrl}
                          onClick={(e) => e.preventDefault()}
                          style={{ textDecoration: 'none' }}
                        >
                          <span style={linkTextStyle}>{privacyPolicyLabel}</span>
                        </a>
                      )}
                    </td>
                    <td width="25%" align="right" valign="middle">
                      {brandliveLogo && <LogoWidget src={brandliveLogo} height={22} align="right" padding="0" />}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
